'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { useNavigationActions } from '@/components/providers/NavigationProvider'
import { usePage } from '@/components/providers/PageProvider'
import { type RouteType } from '@/defs/route'
import { type ScrollType } from '@/defs/scroll'
import { buildRoute, type OptionsProps } from '@/utils/routeUtil'

type RedirectOptionsProps = {
  replace?: boolean
  scroll?: ScrollType
}

export const useLinkInternal = () => {
  const router = useRouter()
  const { page } = usePage()
  const { setScrollRequest, startRedirect } = useNavigationActions()
  const route = page?.route

  const buildRelative = useCallback(
    (routeInput: Partial<RouteType>, options?: OptionsProps) => {
      const section = routeInput.section || route?.section
      if (!section) return ''

      return buildRoute({ ...route, pageNumber: undefined, ...routeInput, section }, options)
    },
    [route],
  )

  const redirectUrl = useCallback(
    (url?: string, options?: RedirectOptionsProps) => {
      if (!url) return
      setScrollRequest(url, options?.scroll)
      startRedirect(() =>
        options?.replace ? router.replace(url, { scroll: false }) : router.push(url, { scroll: false }),
      )
    },
    [setScrollRequest, router, startRedirect],
  )

  const redirectRelative = useCallback(
    (routeInput: Partial<RouteType>, options?: RedirectOptionsProps) => redirectUrl(buildRelative(routeInput), options),
    [buildRelative, redirectUrl],
  )

  return useMemo(
    () => ({ buildRelative, redirectRelative, redirectUrl }),
    [buildRelative, redirectRelative, redirectUrl],
  )
}
